import React from 'react';
import betslip from '@gin-betting/betslip';
import { App, GQL } from '@gin-betting/core';
import cn from 'classnames';
import { includes } from 'lodash';

import { LIVE_SPORTEVENT_STATUSES } from 'app-constants';
import { Icon } from 'components';
import Error from '../Error';
import RestrictionsSingle from '../RestrictionsSingle';
import MatchDetails from './MatchDetails';
import OddDetails from './OddDetails';
import OddIcon from './OddIcon';
import OddValue from './OddValue';
import useOdd from './useOdd';
import useValidOddProps, { ValidOddProps } from './useValidOddProps';

import lock from 'assets/svg/lock.svg';
import './odd.scss';

const {
    constants: { BETSLIP_ODD_STATUSES_TO_PLACE_BET },
} = betslip;

interface PassProps {
    isLast: boolean;
    isFirst: boolean;
}

interface Props extends PassProps {
    betslipOddId: string;
}

interface OddProps extends ValidOddProps, PassProps {}

const Odd: React.FC<OddProps> = ({
    isLast,
    isFirst,
    betslipOdd,
    betslipBet,
    odd,
    market,
    match,
}) => {
    const {
        oddFormat,
        clickOddIconHandler,
        isLocked,
        marketName,
        oddName,
        hasError,
        oddValue,
        errorMsgs,
    } = useOdd({
        betslipOdd,
        betslipBet,
        odd,
        market,
        match,
        isLast,
    });

    const { singleStatus, id: betslipOddId } = betslipOdd;

    const { mode, id } = betslipBet;

    const { status, title, startTime } = match;

    const isSingle = mode === GQL.BetType.SINGLE;

    const isLiveMatch = LIVE_SPORTEVENT_STATUSES.includes(status);

    const isProcessing = singleStatus === App.BetslipStatus.PROCESSING;
    const isSuccess = singleStatus === App.BetslipStatus.SUCCESS;

    const isEditable = includes(
        BETSLIP_ODD_STATUSES_TO_PLACE_BET,
        singleStatus
    );
    const isSuspended = isProcessing || isSuccess;

    const isTicket = isSingle && !isEditable;

    const isShowIcon = isSingle || !isSuspended;

    return (
        <React.Fragment>
            <div
                className={cn('__app-Odd-container', {
                    '__app-Odd-container-is-error': hasError,
                    '__app-Odd-container-is-completed': isSuccess,
                })}
                styleName={cn('container', {
                    'is-ticket': isTicket,
                    'is-first': isFirst,
                    'is-last': isLast,

                    'is-processing': isProcessing,
                    'is-completed': isSuccess,
                    'is-error': hasError,
                    'is-locked': isLocked,

                    'round-bottom-border': isLast || errorMsgs.length,
                })}
            >
                {(isFirst || isTicket) && (
                    <MatchDetails
                        isLive={isLiveMatch}
                        title={title}
                        startTime={startTime}
                    />
                )}
                <div styleName="body" className="__app-Odd-body">
                    <OddValue value={oddValue} oddFormat={oddFormat} />

                    <OddDetails oddName={oddName} marketName={marketName} />

                    {isShowIcon && (
                        <OddIcon
                            status={singleStatus}
                            clickHandler={clickOddIconHandler}
                        />
                    )}
                </div>
                {isLocked && (
                    <div styleName="icon-lock">
                        <Icon
                            glyph={lock}
                            styleName="lock"
                            className="__app-Odd-lock"
                        />
                    </div>
                )}
            </div>
            {isSingle && (
                <RestrictionsSingle
                    betslipBetId={id}
                    betslipOddId={betslipOddId}
                />
            )}
            {errorMsgs.length > 0 && <Error messages={errorMsgs} />}
        </React.Fragment>
    );
};

const OddValidationProps: React.FC<Props> = ({
    betslipOddId,
    isLast,
    isFirst,
}) => {
    const validOdd = useValidOddProps({ betslipOddId });

    if (!validOdd) return null;

    const { betslipOdd, betslipBet, odd, market, match } = validOdd;

    return (
        <Odd
            betslipOdd={betslipOdd}
            betslipBet={betslipBet}
            odd={odd}
            market={market}
            match={match}
            isLast={isLast}
            isFirst={isFirst}
        />
    );
};

export default OddValidationProps;
